import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon, ArrowUpRight } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { COMPANY } from "@/data/site";

 const phoneNumber = "971562973825";
 

const defaultMessage =
  "Hello PRINT ZONE! I'd like to get a custom quote for a printing project. Could you please help me with the details and pricing?";
const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(defaultMessage)}`; 
const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/categories", label: "Categories" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Transparent over hero only on Home page top
  const transparent = !scrolled && pathname === "/";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent"
          : "bg-white/85 dark:bg-[#0B1120]/85 backdrop-blur-xl border-b border-slate-100 dark:border-white/10 shadow-[0_8px_30px_-15px_rgba(15,23,42,0.15)]"
      }`}
    >
      <div className="pz-container flex items-center justify-between h-20">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3">
          <img src={COMPANY.logo} alt="Printzee" className="h-16 w-15 rounded-xl" />
          <div className="leading-tight hidden sm:block">
            <div className={`font-display font-bold text-lg tracking-tight ${transparent ? "text-white" : "text-[#0F172A] dark:text-white"}`}>
              Printzee<span className="text-[#F59E0B]">.ae</span>
            </div>
            <div className={`text-[11px] uppercase tracking-[0.22em] ${transparent ? "text-white/70" : "text-slate-500 dark:text-white/60"}`}>
              Branding · Print · Events
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors ${
                  transparent
                    ? `text-white/85 hover:text-white ${isActive ? "text-white" : ""}`
                    : `text-slate-700 dark:text-white/85 hover:text-[#0F172A] dark:hover:text-white ${isActive ? "text-[#0F172A] dark:text-white" : ""}`
                } ${isActive ? "after:absolute after:-bottom-1.5 after:left-0 after:right-0 after:h-[2px] after:bg-[#F59E0B]" : ""}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className={`w-10 h-10 rounded-full grid place-items-center transition-colors border ${
              transparent
                ? "border-white/20 text-white hover:bg-white/10"
                : "border-slate-200 dark:border-white/10 text-slate-700 dark:text-white/85 hover:bg-slate-50 dark:hover:bg-white/5"
            }`}
            aria-label="Toggle dark mode"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          <Link
            to={whatsappUrl}
            data-testid="navbar-cta-quote"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full pz-btn-gold text-sm"
          >
            Get a Quote <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </Link>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="navbar-menu-toggle"
            className={`lg:hidden p-2 rounded-lg ${transparent ? "text-white" : "text-[#0F172A] dark:text-white"}`}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="navbar-mobile-menu" className="lg:hidden bg-white dark:bg-[#0B1120] border-t border-slate-100 dark:border-white/10 shadow-lg">
          <div className="pz-container py-6 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base font-medium py-2 border-b border-slate-100 dark:border-white/10 ${
                    isActive ? "text-[#0F172A] dark:text-white" : "text-slate-700 dark:text-white/75"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-navbar-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full pz-btn-gold"
            >
              Get a Quote <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
